import { inject, Injectable } from '@angular/core';
import { PaginatedResultState } from '../models/paginated-result-state';
import { NewsItem } from './models/news-item';
import { BehaviorSubject, map, Observable, of, Subject, switchMap } from 'rxjs';
import { NewsService } from './news.service';
import { NewsCategory } from './models/news-category.enum';
import { NEWS_STATE_CONFIG } from './news-state.config';

@Injectable()
export class NewsStateService {
  private readonly initialState: PaginatedResultState<NewsItem> = {
    items: [],
    loading: false,
    hasNextPage: true, // initially assume there are more pages
    error: null
  };

  private stateSubject = new BehaviorSubject<PaginatedResultState<NewsItem>>(this.initialState);
  public state$: Observable<PaginatedResultState<NewsItem>> = this.stateSubject.asObservable();

  private lastCategory: NewsCategory | null = null;
  private endCursor: string | null = null;

  private readonly config = inject(NEWS_STATE_CONFIG);
  private newsService = inject(NewsService);

  private loadNews$ = new Subject<{ category: NewsCategory | null; fetchPolicy?: string }>();

  constructor() {
    this.loadNews$.pipe(
      switchMap(({ category, fetchPolicy }) =>
        this.newsService.getCompanyNews(this.config.pageSize, this.endCursor, category, fetchPolicy)
      )
    ).subscribe({
      next: (connection) => {
        this.endCursor = connection.pageInfo.endCursor;
        this.setState({
          items: [...this.stateSubject.value.items, ...connection.nodes],
          loading: false,
          hasNextPage: connection.pageInfo.hasNextPage,
          error: null
        });
      },
      error: (error) => {
        this.setState({
          loading: false,
          error: error.message
        });
      }
    });

    this.loadNews();
  }

  public loadNews(category: NewsCategory | null = null, fetchPolicy?: string): void {
    this.setState({ loading: true, error: null });
    
    if (this.lastCategory != category) {
      this.lastCategory = category;
      this.clearNews();
    }

    this.loadNews$.next({ category, fetchPolicy });
  }

  loadMore(): void {
    const currentState = this.stateSubject.value;
    if (!currentState.hasNextPage || currentState.loading)
      return;

    this.loadNews(this.lastCategory);
  }

  private setState(partialState: Partial<PaginatedResultState<NewsItem>>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partialState
    });
  }

  private clearNews(): void {
    this.endCursor = null;
    this.setState({
      items: [],
      hasNextPage: true
    });
  }

  getNewsItem(id: string): Observable<NewsItem> {
    const currentState = this.stateSubject.value;
    let item = currentState.items.find(n => n.id === id);
    if (item == null) {
      throw Error;
    }
    return of(item!);
  }

  refreshNews(): void {
    this.clearNews();
    this.loadNews(this.lastCategory, 'network-only');
  }

  // Getters for individual state slices
  get items$(): Observable<NewsItem[]> {
    return this.state$.pipe(map(state => state.items));
  }

  get loading$(): Observable<boolean> {
    return this.state$.pipe(map(state => state.loading));
  }

  get hasNextPage$(): Observable<boolean> {
    return this.state$.pipe(map(state => state.hasNextPage));
  }

  get error$(): Observable<string | null> {
    return this.state$.pipe(map(state => state.error));
  }
}
